import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Github, ExternalLink, Code2, Star } from 'lucide-react';

export default function ProjectsSection({ bigProjects }) {
  const [hoveredProject, setHoveredProject] = useState(null);

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    },
  };

  return (
    <section id="projects" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {bigProjects.title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {bigProjects.subtitle}
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-8"
        >
          {bigProjects.projects.slice(0, 6).map((project, i) => (
            <motion.div
              key={i}
              variants={itemVariant}
              onHoverStart={() => setHoveredProject(i)}
              onHoverEnd={() => setHoveredProject(null)}
              className="group relative h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-secondary-500/10 to-accent-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 h-full flex flex-col hover:shadow-2xl transition-shadow duration-300">
                {/* Project Image */}
                {project.image && (
                  <div className="relative h-48 bg-gradient-to-br from-primary-500 to-secondary-500 overflow-hidden flex items-center justify-center">
                    <motion.img
                      animate={{ scale: hoveredProject === i ? 1.1 : 1 }}
                      transition={{ duration: 0.3 }}
                      src={project.image}
                      alt={project.projectName}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                  </div>
                )}

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                    {project.projectName}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow">
                    {project.projectDesc}
                  </p>

                  {/* Metrics */}
                  {project.metrics && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{
                        opacity: hoveredProject === i ? 1 : 0,
                        height: hoveredProject === i ? 'auto' : 0
                      }}
                      className="mb-4 overflow-hidden"
                    >
                      <div className="p-3 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-lg border border-primary-500/20">
                        <p className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                          <Star className="inline mr-2" size={16} />
                          Impact: {project.metrics}
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Tech Stack */}
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <Code2 className="inline mr-2" size={16} />
                    {project.projectTechStack}
                  </p>

                  {/* Links */}
                  <div className="flex gap-3 mt-auto">
                    {project.projectLink && (
                      <motion.a
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        href={project.projectLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold transition-colors"
                      >
                        <Github size={18} />
                        Code
                      </motion.a>
                    )}
                    {project.projectUrl && (
                      <motion.a
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        href={project.projectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 border-primary-500 text-primary-500 dark:text-primary-400 rounded-lg font-semibold hover:bg-primary-50 dark:hover:bg-slate-700 transition-colors"
                      >
                        <ExternalLink size={18} />
                        Demo
                      </motion.a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* View all projects link */}
        {bigProjects.projects.length > 6 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex justify-center mt-12"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg font-semibold"
            >
              View All {bigProjects.projects.length} Projects
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
